import {
  SurveyBundleI,
  SurveyElement,
  SurveyListOption,
  SurveyPage,
  PageType,
  SurveyBlock,
  ListType,
  Page,
  SurveyAnyPage,
  SurveyEndPage,
} from '@hyper-inferno/survey-elements-core';

/**
 * Updated Survey Domain Class.
 * Adjusted to handle the nested 'jsn_definition' payload structure.
 */
export class SurveyBundle {
  public readonly id: string; // Changed to string based on the UUID in payload
  public readonly version: number;
  public readonly updated_at: number;
  private readonly bundle: SurveyBundleI;

  private elementMap: Map<string | number, SurveyElement> = new Map();
  private optionMap: Map<string | number, SurveyListOption> = new Map();

  constructor(payload: {
    id: string;
    version_number: number;
    jsn_definition: SurveyBundleI;
    created_at: string | number | Date;
  }) {
    // 1. Map root-level metadata
    this.id = payload.id;
    this.version = payload.version_number;

    // 2. Extract the actual survey definition (blocks, lists, etc.)
    // We treat jsn_definition as our internal SurveyBundleI
    this.bundle = payload.jsn_definition;

    // 3. Handle updated_at (prioritizing the one inside the definition)
    this.updated_at =
      this.bundle.updated_at || new Date(payload.created_at).getTime();

    this.buildElementMap();
    this.buildOptionsMap();
  }

  // --- Structural Navigation Logic ---

  /**
   * fetchPageById(pageId)
   * Retrieves a specific page by its ID by searching through all blocks.
   */
  // fetchPageById(pageId: number): SurveyPage | null {
  //   for (const block of this.bundle.blocks) {
  //     const page = block.pages.find((p) => {
  //       return p.id === pageId;
  //     });
  //     if (page) return page;
  //   }
  //   for (const block of this.bundle.endings) {
  //     if (block.page_id === pageId) {
  //       return block;
  //     }
  //   }
  //   return null;
  // }
  // Update the return type signature
  fetchPageById(pageId: number): SurveyPage | any | null {
    if (!this.bundle) return null;

    // 1. Search through standard content blocks
    for (const block of this.bundle.blocks) {
      const page = block.pages.find((p) => p.id === pageId);
      if (page) return page;
    }

    // 2. Fallback: Search through endings if not found in blocks
    // This code only runs if the first loop didn't find a match
    for (const ending of this.bundle.endings) {
      if (ending.id === pageId) {
        return ending;
      }
    }

    return null;
  }
  /**
   * fetchFirstPageQuery()
   *  * Finds the entry point of the survey (the first page of the first block).
   */
  fetchFirstPageQuery(): SurveyAnyPage {
    const firstBlock = this.sortedBlocks[0];
    if (!firstBlock || !firstBlock.pages || !firstBlock.pages.length)
      return this.getFirstEnding();
    return [...firstBlock.pages].sort((a, b) => a.rank - b.rank)[0];
  }

  /**
   * fetchNextPageQuery(currentPageId)
   *  * Determines the next logical page in the hierarchy.
   * Logic: Next page in current block -> First page of next block.
   */
  fetchNextPageQuery(currentPageId: number): SurveyAnyPage {
    const blocks = this.sortedBlocks;
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      if (!block.pages) continue;

      const pages = [...block.pages].sort((a, b) => a.rank - b.rank);
      const pageIndex = pages.findIndex((p) => p.id === currentPageId);

      if (pageIndex !== -1) {
        // 1. Next page in current block
        if (pageIndex < pages.length - 1) {
          return pages[pageIndex + 1];
        }

        // 2. First page of next block
        const nextBlock = blocks[i + 1];
        if (nextBlock && nextBlock.pages && nextBlock.pages.length > 0) {
          return [...nextBlock.pages].sort((a, b) => a.rank - b.rank)[0];
        }
      }
    }
    // No Next Page Found - Return Ending
    return this.getFirstEnding();
  }

  getFirstEnding() {
    return this.bundle.endings[0];
  }

  /**
   * getAllPagesOfSurvey()
   * Returns a complete, flattened, and rank-ordered list of every page.
   */
  getAllPagesOfSurvey(): SurveyPage[] {
    const allPages: SurveyPage[] = [];
    const sBlocks = this.sortedBlocks;
    for (const block of sBlocks) {
      if (block.pages) {
        const sPages = [...block.pages]
          .sort((a, b) => a.rank - b.rank)
          .filter((page) => page.type == PageType.ELEMENT_PAGE_MODEL);
        allPages.push(...sPages);
      }
    }
    return allPages;
  }

  // --- List & Translation Management ---

  /**
   * getListByListIdQuery(listId)
   * Fetches a specific list from the bundle metadata.
   */
  getListByListIdQuery(listId: string | number) {
    const list = this.bundle.lists?.find((l: any) => l.id === listId);
    return list || null;
  }

  /**
   * getListByListIdQuery(listId)
   * Fetches a specific list and its construction instructions from the bundle.
   */
  getInstructionsByListId(listId: string | number) {
    // Find the list in the bundle
    const list = this.bundle.lists?.find((l: any) => l.id === listId);
    if (!list) return null;

    return {
      ...list,
      // Ensure instructions are included (default to empty array if none exist)
      // instructions: list.instructions || [],
    };
  }

  /**
   * getTranslatedTextV2(lang, codexId)
   * Accesses the internal translations dictionary.
   */
  getTranslatedTextV2(lang: string, codexId: string): string {
    const translations = (this.bundle as any).translations;
    if (translations && translations[lang]) {
      return translations[lang][codexId] || codexId;
    }
    return codexId;
  }

  // --- Utility Getters ---

  /**
   * Returns all blocks in the survey sorted by their rank.
   */
  get sortedBlocks(): SurveyBlock[] {
    if (!this.bundle.blocks) return [];
    return [...this.bundle.blocks].sort((a, b) => a.rank - b.rank);
  }

  /**
   * Checks if a new version is available based on server metadata.
   */
  isOutdated(serverVersion: number, serverUpdatedAt: number): boolean {
    return serverVersion > this.version || serverUpdatedAt > this.updated_at;
  }

  /**
   * Returns the raw bundle JSON.
   */
  toJSON(): SurveyBundleI {
    return this.bundle;
  }

  /**
   * Returns the Progress Percentage of the pageId.
   */
  getProgressPercentage(currentPageId: number): number {
    const allPages = this.getAllPagesOfSurvey();
    if (allPages.length === 0) return 0;

    const currentIndex = allPages.findIndex((p) => p.id === currentPageId);

    // If page not found, we haven't started or are in an invalid state
    if (currentIndex === -1) return 0;

    // To show progress *after* completing the current page, use (currentIndex + 1)
    // To show progress *arriving* at the current page, use (currentIndex)
    const progress = Math.round((currentIndex / allPages.length) * 100);

    return Math.min(progress, 100);
  }

  getConditionSetsBySkipElementSettingId(skipElementSettingsId: string) {
    return (this.bundle.condition_sets ?? []).filter(
      (set) => set.skip_logic_id === skipElementSettingsId,
    )?.[0];
  }

  getConditionSetsByElementId(elementId: string) {
    return (this.bundle.condition_sets ?? []).filter(
      (set) => set.condition_set_element_id === Number(elementId),
    )?.[0];
  }

  private buildElementMap() {
    this.bundle.blocks?.forEach((block) => {
      block.pages?.forEach((page) => {
        page.elements?.forEach((el) => {
          this.elementMap.set(el.id, el);
        });
      });
    });
  }

  fetchElementById(elementId: string | number) {
    return this.elementMap.get(elementId) || null;
  }

  private buildOptionsMap() {
    this.bundle.lists?.forEach((list) => {
      list.options?.forEach((option) => {
        this.optionMap.set(option.id, option);
      });
    });
  }

  fetchOptionById(optionId: string | number) {
    return this.optionMap.get(optionId) || null;
  }

  getListOfQuestion(function_question_id: number) {
    // Find the list in the bundle
    const element = this.elementMap.get(function_question_id);
    // TODO, we might need a better parameter mapping system here
    const list = this.getListByListIdQuery(element?.config['list_id']);

    if (list?.list_type == ListType.CONSTRUCTED_LIST) {
      return this.getListByListIdQuery(list.primary_list_id!);
    }
    return list;
  }
}
